
import { defineComponent, reactive, ref, computed } from 'vue'
import mitt from 'mitt'

import { getArtists, getTimeSlots } from '../api'

import TimeSlotList from './TimeSlotList.vue'
import ArtistList from './ArtistList.vue'
import ChangeLog from './ChangeLog.vue'
import About from './About.vue'

export default defineComponent({
  name: 'Home',
  components: {
    TimeSlotList,
    ArtistList,
    ChangeLog,
    About,
  },
  async setup() {
    const emitter = mitt()

    const store = reactive({
      artistList: await getArtists(),
      timeSlotList: await getTimeSlots(),
      selectedArtistId: -1,
      selectedSlotId: -1,
    })

    const unassignedArtists = computed(() =>
      store.artistList.filter(artist => artist.slotId === null),
    )
    const assignedArtists = computed(() =>
      store.artistList.filter(artist => artist.slotId !== null),
    )
    const timezone = ref('Europe/Helsinki')
    const utcOffset = ref(3)
    const setTimezone = ({ tz, offset }: { tz: string; offset: number }): void => {
      timezone.value = tz
      utcOffset.value = offset
      store.selectedSlotId = -1
      console.log('setting timezone to ', tz, offset)
    }
    emitter.on('set-tz', setTimezone)

    const offsetDisplay = computed(() => {
      if (utcOffset.value > -1) {
        return `+${utcOffset.value}`
      } else {
        return `${utcOffset.value}`
      }
    })

    const setSelectedArtist = (artistId: number): void => {
      store.selectedArtistId = artistId

      const artistIndex = store.artistList.findIndex(artist => artist.id === artistId)

      setTimezone({
        tz: store.artistList[artistIndex].timezone,
        offset: store.artistList[artistIndex].utcOffset,
      })
    }
    emitter.on('select-artist', setSelectedArtist)

    const assignArtistToSlot = ({
      artistId,
      slotId,
    }: {
      // prettier-ignore
      artistId: string;
      // prettier-ignore
      slotId: number;
    }): void => {
      const artistIndex = store.artistList.findIndex(artist => artist.id === parseInt(artistId))
      const timeSlotIndex = store.timeSlotList.findIndex(slot => slot.id === slotId)
      store.artistList[artistIndex].slotId = slotId
      store.timeSlotList[timeSlotIndex].artistId = parseInt(artistId)
      store.timeSlotList[timeSlotIndex].status = 'TENTATIVE'
      store.selectedSlotId = -1
    }
    emitter.on('assign-artist', assignArtistToSlot)

    const clearTimeSlot = (slotId: number): void => {
      const timeSlotIndex = store.timeSlotList.findIndex(slot => slot.id === slotId)
      const artistId = store.timeSlotList[timeSlotIndex].artistId
      const artistIndex = store.artistList.findIndex(artist => artist.id === artistId)

      store.timeSlotList[timeSlotIndex].artistId = null
      store.timeSlotList[timeSlotIndex].status = 'EMPTY'
      store.artistList[artistIndex].slotId = null
      store.selectedSlotId = -1
    }
    emitter.on('clear-slot', clearTimeSlot)

    const setTimeSlotStatus = ({
      slotId,
      status,
    }: {
      // prettier-ignore
      slotId: number;
      // prettier-ignore
      status: 'EMPTY' | 'TENTATIVE' | 'CONFIRMED';
    }): void => {
      const timeSlotIndex = store.timeSlotList.findIndex(slot => slot.id === slotId)
      store.timeSlotList[timeSlotIndex].status = status
      store.selectedSlotId = -1
    }
    emitter.on('set-slot-status', setTimeSlotStatus)

    const setSelectedSlot = (slotId: number): void => {
      store.selectedSlotId = slotId
    }
    emitter.on('select-slot', setSelectedSlot)

    return {
      unassignedArtists,
      assignedArtists,
      timezone,
      offsetDisplay,
      setTimezone,
      emitter,
      store,
    }
  },
})
